/**
 * components/charts/Sparkline — a tiny per-trade R-journey spark, built to the dataviz
 * skill: single thin series, no axes or legend (the row's numbers carry the values), a
 * recessive zero baseline for reference, and one selective end dot. Color follows the
 * realized sign (profit-green / loss-red) — the same semantic poles as EquityChart, so
 * the terminal reads as one system.
 *
 * The path is the trade's excursion in risk multiples: entry (0R) → max adverse
 * excursion (−MAE) → max favorable excursion (+MFE) → the realized close. When the
 * excursions are unknown it degrades to entry → close. Pure presentational.
 */
interface SparklineProps {
  /** Realized R at close — sets the color and the end point. */
  readonly realized: number | null;
  /** Max adverse excursion in R (positive magnitude); the trough. */
  readonly mae?: number | null;
  /** Max favorable excursion in R (positive magnitude); the peak. */
  readonly mfe?: number | null;
  readonly width?: number;
  readonly height?: number;
}

const PAD_Y = 3;

export function Sparkline({ realized, mae, mfe, width = 92, height = 26 }: SparklineProps): JSX.Element {
  const r = realized ?? 0;
  // Excursion path in R-space: start flat at entry, dip to −MAE, rise to +MFE, close at R.
  const series: number[] = [0];
  if (mae != null) series.push(-Math.abs(mae));
  if (mfe != null) series.push(Math.abs(mfe));
  series.push(r);

  const lo = Math.min(0, ...series);
  const hi = Math.max(0, ...series);
  const span = hi - lo || 1;

  const stepX = series.length > 1 ? (width - 2) / (series.length - 1) : 0;
  const x = (i: number): number => 1 + i * stepX;
  const y = (v: number): number => PAD_Y + (1 - (v - lo) / span) * (height - 2 * PAD_Y);

  const path = series.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');
  const color = r > 0 ? 'var(--profit)' : r < 0 ? 'var(--loss)' : 'var(--text-muted)';
  const zeroY = y(0);
  const endX = x(series.length - 1);
  const endY = y(r);

  const label = `Trade R journey: entry 0R${mae != null ? `, low −${Math.abs(mae).toFixed(1)}R` : ''}${
    mfe != null ? `, high +${Math.abs(mfe).toFixed(1)}R` : ''
  }, closed ${r >= 0 ? '+' : '−'}${Math.abs(r).toFixed(2)}R`;

  return (
    <svg className="spark" width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label}>
      <line x1={1} x2={width - 1} y1={zeroY} y2={zeroY} stroke="var(--viz-grid)" strokeWidth={1} />
      <path d={path} fill="none" stroke={color} strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={endX} cy={endY} r={2.2} fill={color} />
    </svg>
  );
}
